import { useSelector } from 'react-redux';
import { useAppDispatch } from '../app/hooks';
import { selectOrderMessageData } from '../redux/features/sessionBoundary/sessionBoundary.selectors';
import {
  selectCartItems,
  selectHypothesisCartItems,
  selectValidCartItems,
} from '../selectors/cart';
import { startSendOrder, populateOrderRequests } from '../reducers/orderSlice';
import { ISendOrderData } from '../reducers/orderSlice.props';
import { selectIsAIAutoMode } from '../redux/features/ai/ai.selector';
import { useEffect, useRef } from 'react';
import { selectFeatureFlagLoyaltySupport } from '../redux/features/config/config.selector';

const useExternalOrderHandler = () => {
  const dispatch = useAppDispatch();
  const orderMessageData = useSelector(selectOrderMessageData);
  const cartItems = useSelector(selectCartItems);
  const isAIAutoMode = useSelector(selectIsAIAutoMode);
  const isOrderFromExternal = isAIAutoMode;
  const hypothesisCartItems = useSelector(selectHypothesisCartItems);
  const FEATURE_FLAG_LOYALTY_SUPPORT = useSelector(
    selectFeatureFlagLoyaltySupport
  );

  // HITL-1513: the request_id of the final order this session has already
  // populated, so it can never be populated twice.
  //
  // Two things combine into a bug without it. `sessionBoundary.orderMessageData`
  // OUTLIVES the order it describes — only fillEndSessionValues clears it, which
  // needs the car to have exited — and this effect deliberately re-runs on every
  // cart change, so that an order event arriving before its cart items still
  // records the right cartItemIds. So when the final order's check lands,
  // resetStatesAfterFinalOrderSubmit fires clearCart(), that cart change re-runs
  // this effect, and it re-populates the SAME, already-completed final order with
  // checkResponse: null and a fresh orderSentAt. POS has already answered that
  // request_id, so nothing can ever settle the new entry: the in-flight banner
  // counts up forever, and useOrderCheckRetry's overdue clock is armed for an
  // order that finished. (With loyalty support on, the empty-cart guard below
  // does not stop it, which is why it reproduces every time on those stores.)
  //
  // Scoped to finals on purpose. Interim orders keep the re-run — that is how
  // their cartItemIds fill in — and a final order does not need it, because the
  // cart is complete by the time one is sent.
  const populatedFinalRequestIdRef = useRef('');

  useEffect(() => {
    // fillEndSessionValues empties orderMessageData at session end, so this is
    // the session boundary as this hook sees it. Clearing here scopes the guard
    // below to ONE session rather than to the component's lifetime — the next
    // car's final order can never be mistaken for a repeat of the last one's.
    // Within a session nothing empties orderMessageData (that is precisely the
    // bug being fixed), so the guard still holds exactly where it must.
    if (!Object.values(orderMessageData).length) {
      populatedFinalRequestIdRef.current = '';
      return;
    }

    const externalOrderItems = { ...cartItems, ...hypothesisCartItems };
    if (
      isOrderFromExternal &&
      Object.values(orderMessageData).length &&
      (Object.values(externalOrderItems).length || FEATURE_FLAG_LOYALTY_SUPPORT)
    ) {
      const {
        request_id = '',
        final = false,
        coupons = [],
        loyalties = [],
      } = orderMessageData as ISendOrderData;

      // Ahead of startSendOrder, so a repeat does not re-mark the (just
      // cleared) cart lines as sending either.
      if (final && request_id) {
        if (populatedFinalRequestIdRef.current === request_id) return;
        populatedFinalRequestIdRef.current = request_id;
      }

      const cartItemIds = Object.values(externalOrderItems)
        .reduce((acc: number[], { cartItemId }) => {
          if (cartItemId) acc.push(cartItemId);
          return acc;
        }, [])
        .sort();

      dispatch(startSendOrder(cartItemIds));

      dispatch(
        populateOrderRequests({
          requestId: request_id,
          cartItemIds,
          origin: 'AI',
          isFinal: final,
          couponIds: Object.keys(coupons),
          loyalties,
          // Client receipt time of the AI's order. Without it the overdue
          // clock (useOrderCheckRetry) can never start, so an agent taking
          // over a session whose last AI order is stuck would get no Retry
          // button — the strongest case for having one.
          orderSentAt: new Date().toISOString(),
        })
      );
    }
  }, [orderMessageData, cartItems, hypothesisCartItems]);
};

export default useExternalOrderHandler;
